import type { FingerName, HandShapeName, LocationName, NonManual, PalmDirection } from './types';

export const FINGERS: FingerName[] = ['thumb', 'index', 'middle', 'ring', 'little'];

export interface HandShape {
  /** Per-finger closure, 0 straight to 1 fully curled. */
  curl: Record<FingerName, number>;
  /** 0 fingers together, 1 fully splayed. */
  spread: number;
  /** 0 thumb tucked across the palm, 1 thumb held out. */
  thumbOut: number;
}

const shape = (
  curl: [number, number, number, number, number],
  spread: number,
  thumbOut: number,
): HandShape => ({
  curl: { thumb: curl[0], index: curl[1], middle: curl[2], ring: curl[3], little: curl[4] },
  spread,
  thumbOut,
});

export const HAND_SHAPES: Record<HandShapeName, HandShape> = {
  //              thumb index  mid  ring little  spread thumbOut
  relaxed: shape([0.35, 0.3, 0.35, 0.4, 0.45], 0.15, 0.4),
  flat: shape([0.1, 0.0, 0.0, 0.0, 0.0], 0.02, 0.25),
  spread: shape([0.05, 0.0, 0.0, 0.0, 0.0], 1.0, 0.9),
  fist: shape([0.75, 1.0, 1.0, 1.0, 1.0], 0.0, 0.15),
  point: shape([0.7, 0.0, 1.0, 1.0, 1.0], 0.1, 0.25),
  curved: shape([0.45, 0.5, 0.5, 0.5, 0.5], 0.2, 0.55),
  pinch: shape([0.55, 0.6, 0.85, 0.95, 1.0], 0.1, 0.7),
  bent: shape([0.3, 0.55, 0.55, 0.55, 0.55], 0.05, 0.35),
  // The consonant shapes of the BANZSL manual alphabet.
  circle: shape([0.5, 0.55, 0.05, 0.05, 0.05], 0.55, 0.85),
  cee: shape([0.3, 0.45, 1.0, 1.0, 1.0], 0.05, 0.8),
  two: shape([0.7, 0.0, 0.0, 1.0, 1.0], 0.02, 0.2),
  vee: shape([0.7, 0.0, 0.0, 1.0, 1.0], 0.45, 0.2),
  three: shape([0.7, 0.0, 0.0, 0.0, 1.0], 0.02, 0.2),
  hook: shape([0.6, 0.5, 1.0, 1.0, 1.0], 0.05, 0.2),
  littleOut: shape([0.75, 1.0, 1.0, 1.0, 0.0], 0.1, 0.15),
  flatBent: shape([0.2, 0.5, 0.5, 0.5, 0.5], 0.02, 0.25),
};

/**
 * Signing-space anchors, given for the dominant (right) hand in body units:
 * x right, y up, z forward. The weak hand mirrors x.
 */
export const LOCATIONS: Record<LocationName, [number, number, number]> = {
  rest: [0.24, 1.03, 0.1],
  neutral: [0.16, 1.24, 0.26],
  chest: [0.1, 1.26, 0.14],
  shoulder: [0.19, 1.4, 0.08],
  chin: [0.06, 1.5, 0.16],
  mouth: [0.05, 1.53, 0.16],
  cheek: [0.12, 1.57, 0.12],
  temple: [0.14, 1.65, 0.08],
  forehead: [0.07, 1.68, 0.12],
  self: [0.05, 1.3, 0.3],
  forward: [0.14, 1.34, 0.42],
  // Resolved at runtime against the weak hand's actual fingertips.
  weakHand: [0.0, 1.26, 0.3],
};

export const PALM_NORMALS: Record<PalmDirection, [number, number, number]> = {
  down: [0, -1, 0],
  up: [0, 1, 0],
  in: [0, 0, -1],
  out: [0, 0, 1],
  left: [-1, 0, 0],
  right: [1, 0, 0],
};

export const NEUTRAL_NMF: Required<NonManual> = {
  brow: 0,
  headTilt: 0,
  headNod: 0,
  headTurn: 0,
  mouthOpen: 0,
};

export function lerp(from: number, to: number, amount: number): number {
  return from + (to - from) * amount;
}

/** Ease in and out so a sign settles into its hold rather than snapping. */
export function easeInOut(t: number): number {
  const clamped = Math.min(1, Math.max(0, t));
  return clamped < 0.5
    ? 4 * clamped * clamped * clamped
    : 1 - Math.pow(-2 * clamped + 2, 3) / 2;
}

export function blendShapes(from: HandShape, to: HandShape, amount: number): HandShape {
  const curl = {} as Record<FingerName, number>;
  for (const finger of FINGERS) curl[finger] = lerp(from.curl[finger], to.curl[finger], amount);
  return {
    curl,
    spread: lerp(from.spread, to.spread, amount),
    thumbOut: lerp(from.thumbOut, to.thumbOut, amount),
  };
}

export function blendNonManual(
  from: Required<NonManual>,
  to: Required<NonManual>,
  amount: number,
): Required<NonManual> {
  return {
    brow: lerp(from.brow, to.brow, amount),
    headTilt: lerp(from.headTilt, to.headTilt, amount),
    headNod: lerp(from.headNod, to.headNod, amount),
    headTurn: lerp(from.headTurn, to.headTurn, amount),
    mouthOpen: lerp(from.mouthOpen, to.mouthOpen, amount),
  };
}

export function withDefaults(nmf: NonManual | undefined): Required<NonManual> {
  return { ...NEUTRAL_NMF, ...nmf };
}
